import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import createApolloClient from 'components/withApollo/createApolloClient'
import NotFoundPage from 'components/pages/NotFoundPage'
import ErrorPage from 'components/pages/ErrorPage'
import {
  topTrucksQuery,
  topTrucksQueryVariables
} from 'components/pages/TopTrucksPage/types/topTrucksQuery'
import format from 'date-fns/format'
import Truck from 'components/pages/TopTrucksPage/Truck'
import { sortBy } from 'lodash'
import { topTrucksMarketPathsQuery } from 'components/pages/TopTrucksPage/types/topTrucksMarketPathsQuery'
import CtaSection from 'components/pages/BlogPostPage/CtaSection'
import CtaButton from 'components/ui/CtaButton'

import QUERY from './query'
import PATHS_QUERY from './topTrucksMarketPathsQuery'

type Props = {}

type Params = {
  market: string
}

const TopTrucksPage: NextPage = () => {
  const router = useRouter()

  const { market: slug } = router.query as Params

  const { data, error } = useQuery<topTrucksQuery, topTrucksQueryVariables>(
    QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        slug
      }
    }
  )

  if (error) return <ErrorPage message={error.message} />
  if (!data?.marketBySlug) {
    return <NotFoundPage search={slug} />
  }

  const market = data.marketBySlug
  const marketTopTrucks = sortBy(market.marketTopTrucks, (m) => -m.position)
  return (
    <PageLayout fullWidth staticPage>
      <PageMeta
        canonicalPath={market.customerTopTrucksPath}
        coordinates={[market.latitude, market.longitude]}
        image={marketTopTrucks[0]?.truck.image.medium}
        keywords={`food trucks, top, ${market.name}, ${market.stateFull}, catering`}
        ldJson={data.structuredData.marketTopTrucksPage}
        title={`Best Food Trucks | Best-${market.marketTopTrucks.length} Food Trucks in ${market.name}`}
      />
      <div className="w-full">
        <div className="prose md:prose-lg lg:prose-xl mt-24 mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
          <p className="text-base text-center font-semibold tracking-wider text-orange-600 uppercase">
            {format(new Date(), 'MMMM yyyy')}
          </p>
          <h1 className="text-center">
            Top-{market.marketTopTrucks.length} Food Trucks in {market.name}
          </h1>
          {market.topTrucksDescription?.html && (
            <div
              className="text-justify"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                // eslint-disable-next-line react/no-danger
                __html: market.topTrucksDescription.html
              }}
            />
          )}
          <div className="mx-auto sm:max-w-max">
            <CtaButton
              onClick={() => router.push('/hire-food-truck')}
              fullWidth
            >
              Hire a Food Truck For Catering
            </CtaButton>
          </div>
        </div>
        <div className="mt-8">
          {marketTopTrucks.map((truck) => (
            <Truck key={truck.id} truck={truck} />
          ))}
        </div>
        <CtaSection />
      </div>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(TopTrucksPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<topTrucksMarketPathsQuery>({
    query: PATHS_QUERY
  })

  const paths = data.customerPaths.marketTopTrucksPaths.map((marketPath) => ({
    params: { market: marketPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  TopTrucksPage,
  {
    revalidate: 60 * 30 // 30 minutes
  }
)
